import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.8, rotate: -5 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

// Hover effect for links
const linkHoverEffect = {
  whileHover: {
    scale: 1.05,
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.1)",
    transition: { duration: 0.3 },
  },
};

const underlineVariants = {
  hidden: { width: 0 },
  visible: { width: "100%", transition: { duration: 0.3 } },
};

const TransitionEffect = ({ isInView }) => (
  <motion.div
    className="absolute inset-0 bg-gradient-to-r from-[#ff4d5a] to-[#0d2f55] dark:from-[#ff7d85] dark:to-[#272729] rounded-lg"
    initial={{ opacity: 0 }}
    animate={isInView ? { opacity: 0.5 } : { opacity: 0 }}
    whileHover={{ opacity: 0.8 }}
    transition={{ duration: 0.5, ease: "easeInOut" }}
  />
);

const AboutMe = () => {
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  const { ref: imageRef, inView: isImageInView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  return (
    <motion.section
      ref={sectionRef}
      className="relative flex flex-col md:flex-row-reverse pt-8 p-4 sm:p-6 md:p-8 lg:p-16 items-center overflow-hidden min-h-screen"
      variants={staggerContainer}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      {/* Profile Image (mobile and desktop) */}
      <motion.div
        className="w-full md:w-1/2 relative overflow-hidden z-10"
        variants={imageVariants}
        initial="hidden"
        animate={isImageInView ? "visible" : "hidden"}
        ref={imageRef}
      >
        <div className="relative w-full h-full bg-cover bg-center">
          <motion.img
            src="/Image/AboutME.webp"
            className="rounded-lg w-full h-80 sm:h-96 md:h-[28rem] lg:h-[32rem] xl:h-[36rem] object-cover shadow-lg transition-opacity duration-500"
            alt="Profile"
            whileHover={{ scale: 1.15, rotate: 5, boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)" }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
          <TransitionEffect isInView={isImageInView} />
          <div className="absolute inset-0 opacity-10 bg-[#072142]"></div>
          <div className="absolute inset-0 opacity-5 bg-[#ff4d5a]"></div>
        </div>
      </motion.div>

      {/* Text Section */}
      <motion.div className="w-full md:w-1/2 flex flex-col z-10 text-center md:text-left" variants={fadeInUp}>
        <motion.h2
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-dark dark:text-white leading-tight"
          variants={fadeInUp}
        >
          About Me
        </motion.h2>

        <motion.div className="flex justify-center md:justify-start items-center mt-4 mb-6" variants={fadeInUp}>
          <span className="block w-20 h-1 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] transition-all"></span>
          <span className="block w-20 h-1 bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] ml-4 transition-all"></span>
        </motion.div>

        <motion.p
          className="text-lg sm:text-xl font-medium text-dark dark:text-gray-200 mb-6 transition-opacity duration-300 hover:opacity-90"
          variants={fadeInUp}
        >
          I am a MERN stack web developer with over 2 months of experience as a React intern at Celebel. Passionate about building intuitive and dynamic web applications, I leverage my expertise in JavaScript and ReactJS to create seamless user experiences.
        </motion.p>

        {/* Links Section */}
        <motion.ul className="flex flex-col space-y-4 mx-2" variants={fadeInUp}>
          {[
            {
              href: "/Kailash.pdf",
              label: "Resume",
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16" className="mr-2 h-6 w-6">
                  <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0zM9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1zM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm2 5.755V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-.245S4 12 8 12s5 1.755 5 1.755z"></path>
                </svg>
              ),
            },
            {
              href: "https://www.linkedin.com/in/kailash-agarwal-657823235/",
              label: "LinkedIn",
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" className="mr-2 h-6 w-6">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              ),
            },
            {
              href: "https://github.com/Animatedguess",
              label: "GitHub",
              icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 h-6 w-6" viewBox="0 0 24 24">
                  <path d="M12 1C5.93 1 1 5.93 1 12c0 4.97 3.47 9.13 8.26 10.61.6.11.82-.26.82-.58v-2.08c-3.34.72-4.04-1.61-4.04-1.61-.55-1.4-1.34-1.77-1.34-1.77-1.09-.74.08-.73.08-.73 1.2.09 1.83 1.23 1.83 1.23 1.08 1.85 2.84 1.32 3.54 1.01.11-.78.42-1.32.76-1.62-2.66-.3-5.46-1.34-5.46-5.97 0-1.32.47-2.4 1.24-3.24-.12-.3-.54-1.52.12-3.16 0 0 1.01-.32 3.3 1.24a11.5 11.5 0 0 1 6.02 0c2.3-1.56 3.3-1.24 3.3-1.24.66 1.64.24 2.85.12 3.16.78.84 1.24 1.92 1.24 3.24 0 4.64-2.81 5.66-5.49 5.96.43.37.81 1.1.81 2.21v3.27c0 .32.22.69.82.58A10.004 10.004 0 0 0 23 12c0-6.07-4.93-11-11-11z"></path>
                </svg>
              ),
            },
          ].map(({ href, label, icon }, idx) => (
            <motion.li key={idx} className="group relative inline-block">
              <motion.a
                href={href}
                className="flex items-center text-lg sm:text-xl font-semibold text-dark dark:text-white hover:text-red-600 dark:hover:text-red-400 transition-transform"
                variants={linkHoverEffect}
                whileHover="whileHover"
              >
                {icon}
                {label}
              </motion.a>
              <motion.span
                className="absolute left-0 bottom-[-2px] h-[2px] bg-gradient-to-r from-[#ff4d5a] to-[#ff934f] dark:from-[#ff7d85] dark:to-[#ffae83] w-0 group-hover:w-full transition-all duration-300"
                initial="hidden"
                animate="visible"
                variants={underlineVariants}
              ></motion.span>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </motion.section>
  );
};

export default AboutMe;
